<script setup lang="ts">
/**
 * Rettelseslaget i vidensbanken ([D-478]): lægger Dokumentationsvagtens
 * tekstrettelser på dokumentationssiden, der vises lige nu.
 *
 * Sidernes tekst er kildekode, så vagten kan ikke rette den i prod; den gemmer
 * i stedet en ordret passage og den rettede tekst (server/src/lib/
 * doc-page-patches.ts). Komponenten står i App.vue ved RouterView og gør 3 ting:
 *   1. henter sidens aktive rettelser (GET /api/doc-patches?path=),
 *   2. skifter passagen ud i sidens tekstnoder (kun nodeValue, så Vue ejer
 *      DOM'en som før; gentegner Vue noden, lægges rettelsen på igen),
 *   3. markerer de rettede passager (CSS Custom Highlight API) og viser et
 *      banner øverst på siden med hver rettelse og dens provenans ([D-107]).
 * Sider uden rettelser er uberørte. Næste session folder rettelserne ind i
 * koden (npm run docs:fold-patches), hvorefter laget ikke længere finder dem.
 */

import { DOC_PAGES, docPatchRegex } from '@cockpit/shared';
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';

import { formatEpochDateTimeDa } from '../../lib/datetime';
import { fetchJson } from '../../lib/http';

interface DocPatchDto {
  id: number;
  find: string;
  replace: string;
  claim: string;
  reality: string;
  evidence: string;
  model: string | null;
  createdAt: number;
}

const HIGHLIGHT = 'doc-patch';

const route = useRoute();
const page = computed(() => DOC_PAGES.find((p) => p.path === route.path) ?? null);
const patches = ref<DocPatchDto[]>([]);
const applied = ref<Set<number>>(new Set());
const slot = ref<HTMLElement | null>(null);
const open = ref(false);

/** Den værdi laget selv har skrevet i noden (så en tilføjelse ikke lægges på 2 gange). */
let ours = new WeakMap<Text, string>();
/** Rettede intervaller pr. node, til markeringen. */
let marked = new Map<Text, Array<[number, number]>>();
let observer: MutationObserver | null = null;
let frame = 0;

function reset(): void {
  patches.value = [];
  applied.value = new Set();
  ours = new WeakMap();
  marked = new Map();
  open.value = false;
  clearHighlight();
}

async function load(): Promise<void> {
  const p = page.value;
  reset();
  if (!p) return;
  try {
    const res = await fetchJson<{ patches: DocPatchDto[] }>(`/api/doc-patches?path=${encodeURIComponent(p.path)}`);
    if (page.value?.key !== p.key) return;
    patches.value = res.patches;
    schedule();
  } catch {
    // Uden rettelser er siden blot som i koden.
  }
}

/** Sidens rod (alle dokumentationssider har .doc-print-page som rod). */
function pageRoot(): HTMLElement | null {
  const roots = document.querySelectorAll<HTMLElement>('main .doc-print-page');
  return roots.length ? roots[roots.length - 1]! : null;
}

function ensureSlot(root: HTMLElement): void {
  if (slot.value && slot.value.isConnected && slot.value.parentElement === root) return;
  const el = document.createElement('div');
  el.className = 'doc-patch-slot print:hidden';
  root.prepend(el);
  slot.value = el;
}

function scan(): void {
  frame = 0;
  if (!patches.value.length) return;
  const root = pageRoot();
  if (!root) return;
  ensureSlot(root);
  const rules = patches.value.map((p) => ({ p, re: docPatchRegex(p.find, '') }));
  const hit = new Set(applied.value);
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  for (let n = walker.nextNode(); n; n = walker.nextNode()) {
    const t = n as Text;
    if (slot.value?.contains(t)) continue;
    const v = t.nodeValue ?? '';
    if (v.length < 12 || ours.get(t) === v) continue;
    let out = v;
    const here: DocPatchDto[] = [];
    for (const { p, re } of rules) {
      const m = re.exec(out);
      if (!m) continue;
      out = out.slice(0, m.index) + p.replace + out.slice(m.index + m[0].length);
      here.push(p);
    }
    if (!here.length) continue;
    ours.set(t, out);
    t.nodeValue = out;
    marked.set(
      t,
      here.map((p) => {
        const i = out.indexOf(p.replace);
        return [i, i + p.replace.length] as [number, number];
      }),
    );
    for (const p of here) hit.add(p.id);
  }
  if (hit.size !== applied.value.size) applied.value = hit;
  paintHighlight();
}

function schedule(): void {
  if (!frame) frame = requestAnimationFrame(scan);
}

type HighlightRegistry = Map<string, unknown>;
const highlights = (): HighlightRegistry | null =>
  (globalThis as unknown as { CSS?: { highlights?: HighlightRegistry } }).CSS?.highlights ?? null;

function paintHighlight(): void {
  const reg = highlights();
  const Ctor = (globalThis as unknown as { Highlight?: new (...r: Range[]) => unknown }).Highlight;
  if (!reg || !Ctor) return;
  const ranges: Range[] = [];
  for (const [t, rs] of marked) {
    if (!t.isConnected || ours.get(t) !== t.nodeValue) {
      marked.delete(t);
      continue;
    }
    for (const [a, b] of rs) {
      if (a < 0) continue;
      const r = document.createRange();
      r.setStart(t, a);
      r.setEnd(t, b);
      ranges.push(r);
    }
  }
  reg.set(HIGHLIGHT, new Ctor(...ranges));
}

function clearHighlight(): void {
  highlights()?.delete(HIGHLIGHT);
}

onMounted(() => {
  const main = document.querySelector('main');
  if (main) {
    observer = new MutationObserver(() => {
      if (patches.value.length) schedule();
    });
    observer.observe(main, { childList: true, subtree: true, characterData: true });
  }
  void load();
});

watch(() => page.value?.key, () => void load());

onBeforeUnmount(() => {
  observer?.disconnect();
  if (frame) cancelAnimationFrame(frame);
  clearHighlight();
});

const fmt = (ms: number): string => formatEpochDateTimeDa(ms, { relative: false, seconds: false });
</script>

<template>
  <Teleport v-if="slot && patches.length" :to="slot">
    <div class="rounded-lg border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-sm">
      <p class="font-medium text-amber-700 dark:text-amber-400">
        Dokumentationsvagten har rettet {{ patches.length === 1 ? '1 passage' : `${patches.length} passager` }} på siden
      </p>
      <p class="mt-1 text-amber-700/90 dark:text-amber-400/90">
        Teksten var forældet i forhold til koden eller beslutningerne, så vagten rettede den selv. De rettede passager er
        markeret, og rettelserne lægges ind i sidens kode ved næste udviklingssession.
        <button type="button" class="underline underline-offset-2 hover:text-amber-800 dark:hover:text-amber-300" @click="open = !open">
          {{ open ? 'Skjul rettelserne' : 'Vis rettelserne' }}
        </button>
      </p>
      <ol v-if="open" class="mt-3 space-y-3 list-decimal pl-5 text-ink">
        <li v-for="p in patches" :key="p.id">
          <p><span class="text-muted">Før:</span> <span class="line-through decoration-amber-500/60">{{ p.find }}</span></p>
          <p class="mt-0.5"><span class="text-muted">Nu:</span> {{ p.replace }}</p>
          <p class="mt-0.5 text-xs text-muted">
            {{ p.reality }}
            <template v-if="p.evidence"> Belæg: {{ p.evidence }}.</template>
            Rettet {{ fmt(p.createdAt) }}<template v-if="p.model"> af {{ p.model }}</template>.
            <template v-if="!applied.has(p.id)"> Passagen er ikke på skærmen lige nu (fx i en lukket boks eller en fane).</template>
          </p>
        </li>
      </ol>
    </div>
  </Teleport>
</template>

<style>
::highlight(doc-patch) {
  background-color: rgb(245 158 11 / 0.22);
}
</style>
